import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api, { dnoAPI, customersAPI, authAPI } from '../lib/api';
import { runProposalJob } from '../lib/proposalJob';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Loader2, Plus, RefreshCw, Search, Zap, FileText, Upload, ExternalLink,
  CheckCircle2, Clock, AlertTriangle, XCircle, Send, ChevronRight, RotateCw, Trash2,
  Pencil, X as XIcon, FilePen, Eye, FileWarning, Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { EmailModeToggle } from '../components/EmailModeToggle';
import { EmailPreviewDialog } from '../components/EmailPreviewDialog';
import { SldGenerateDialog } from '../components/SldGenerateDialog';
import { BoundaryMapDialog } from '../components/BoundaryMapDialog';
import { resolveBodyHtml } from '../lib/emailEditor';

const STATUS_STYLES = {
  'Draft': 'bg-slate-100 text-slate-700 border-slate-300',
  'Awaiting Assessment': 'bg-blue-50 text-blue-700 border-blue-200',
  'Scanning Cut-Out Image': 'bg-blue-50 text-blue-700 border-blue-200',
  'Under Assessment': 'bg-amber-50 text-amber-700 border-amber-200',
  'Escalated to DNO': 'bg-orange-50 text-orange-700 border-orange-200',
  'Auto-Approved': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'DNO Approved': 'bg-green-50 text-green-700 border-green-200',
  'DNO Approved with Limit': 'bg-lime-50 text-lime-700 border-lime-200',
  'Further Information Required': 'bg-yellow-50 text-yellow-700 border-yellow-200',
  'DNO Works Required': 'bg-orange-50 text-orange-700 border-orange-200',
  'Failed too Large': 'bg-red-50 text-red-700 border-red-200',
  'Failed': 'bg-red-50 text-red-700 border-red-200',
  'Failed - Invalid Cut-Out Image': 'bg-red-50 text-red-700 border-red-200',
  'Image Scan Failed': 'bg-red-50 text-red-700 border-red-200',
  'Commissioning Submitted': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Commissioning Accepted': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Commissioning Update Required': 'bg-yellow-50 text-yellow-700 border-yellow-200',
  '(Re) Auto-Approved': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  '(Re) Escalated to DNO': 'bg-orange-50 text-orange-700 border-orange-200',
  'Ex Mpan Provided': 'bg-teal-50 text-teal-700 border-teal-200',
};

const StatusIcon = ({ status }) => {
  if (!status) return null;
  if (status.includes('Approved') || status === 'Commissioning Accepted' || status === 'Ex Mpan Provided')
    return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
  if (status.includes('Failed') || status === 'Image Scan Failed')
    return <XCircle className="w-4 h-4 text-red-500" />;
  if (status.includes('Escalated') || status.includes('Information') || status.includes('Update') || status.includes('Works Required'))
    return <AlertTriangle className="w-4 h-4 text-amber-500" />;
  return <Clock className="w-4 h-4 text-blue-500" />;
};

const LCT_LABELS = {
  SOLAR_PV: 'Solar PV', BATTERY: 'Battery', HP: 'Heat Pump',
  EVCP_AC: 'EV Charger (AC)', EVCP_DC: 'EV Charger (DC)', EVV2G: 'EV V2G',
  ACU: 'Air Conditioning', EB: 'Electric Boiler',
};

// ENA Connect Direct accepted enum for devicesToInstall[].generatorType (Battery options)
// Source: validation error response from ENA API on 2026-05-12.
const BATTERY_CHEMISTRIES = [
  'Storage - Electrochemical Classic Batteries -Lithium Ion (Li\u2013ion)',
  'Storage - Electrochemical Classic Batteries -Lithium Polymer (Li-Polymer)',
  'Storage - Electrochemical Classic Batteries -Lithium Sulphur (Li-S)',
  'Storage - Electrochemical Classic Batteries -Lead Acid',
  'Storage - Electrochemical Classic Batteries -Sodium Ion (Na\u2013ion)',
  'Storage - Electrochemical Classic Batteries -Sodium Nickle Chloride (Na-NiCl2)',
  'Storage - Electrochemical Classic Batteries -Sodium Sulphur (Na-S)',
  'Storage - Electrochemical Classic Batteries -Nickle Cadmium (Ni-Cd)',
  'Storage - Electrochemical Classic Batteries -Nickle \u2013Metal Hydride (Ni-MH)',
  'Storage - Electrochemical Classic Batteries -Metal Air',
  'Storage - Electrochemical Flow Batteries - Vanadium Red-Oxide',
  'Storage - Electrochemical Flow Batteries - Zinc \u2013 Iron (Zn \u2013Fe)',
  'Storage - Electrochemical Flow Batteries - Zinc \u2013 Bromine (Zn \u2013Br)',
];

export default function DNOApplications() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // Customer pre-selection from a deep-link (e.g. the "Raise DNO" button on
  // the Customer Detail page navigates here with ?customer_id=...&new=1).
  // Read it ONCE on mount, then strip the params from the URL so a refresh
  // doesn't reopen the wizard.
  const [presetCustomerId, setPresetCustomerId] = useState(() => searchParams.get('customer_id') || '');
  const [detailApp, setDetailApp] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  // Failed submissions (stored locally when ENA rejected the payload)
  const [failedApps, setFailedApps] = useState([]);
  const [failedLoading, setFailedLoading] = useState(false);
  const [failedDetail, setFailedDetail] = useState(null);
  const [retryingId, setRetryingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [activeTab, setActiveTab] = useState('submitted');

  const [loadError, setLoadError] = useState(null);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = { limit: 100, ascending: 'desc' };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await dnoAPI.listApplications(params, {});
      setApplications(res.data?.Items || []);
    } catch (err) {
      // Surface the real reason — usually a 401 from ENA Connect Direct when
      // the API key has been rotated / expired upstream. Hiding it behind a
      // generic toast just means staff stare at a blank page wondering why.
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let message;
      if (status === 401 || /unauthor/i.test(String(detail || ''))) {
        message = 'ENA Connect Direct rejected the API key (HTTP 401). Refresh ENA_API_KEY in the backend .env and restart the backend.';
      } else if (status === 503) {
        message = 'ENA Connect Direct is not configured — set ENA_API_URL / ENA_API_KEY / ENA_TENANT_ID in .env.';
      } else {
        message = detail || err.message || 'Failed to load applications';
      }
      setLoadError({ status, message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchFailedApplications = useCallback(async () => {
    setFailedLoading(true);
    try {
      const res = await dnoAPI.listFailedApplications();
      setFailedApps(res.data || []);
    } catch {
      toast.error('Failed to load failed applications');
    } finally {
      setFailedLoading(false);
    }
  }, []);

  useEffect(() => {
    dnoAPI.status().then(r => setConfigured(r.data?.configured)).catch(() => {});
    fetchApplications();
    fetchFailedApplications();
  }, [fetchApplications, fetchFailedApplications]);

  // Auto-open the New Application wizard when arrived via a deep-link from
  // the Customer Detail page's "Raise DNO" button. Strip the URL params
  // after opening so the dialog doesn't reopen on a refresh.
  useEffect(() => {
    const wantsNew = searchParams.get('new') === '1' || !!searchParams.get('customer_id');
    if (wantsNew) {
      setCreateOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      next.delete('customer_id');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetryFailed = async (id) => {
    setRetryingId(id);
    try {
      await dnoAPI.retryFailedApplication(id);
      toast.success('Application re-submitted successfully');
      fetchApplications();
      fetchFailedApplications();
      setFailedDetail(null);
      setActiveTab('submitted');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Retry failed';
      toast.error(`Retry failed: ${typeof detail === 'string' ? detail.slice(0, 300) : 'Unknown'}…`, { duration: 10000 });
      // refresh in case the error body on the existing record was updated
      fetchFailedApplications();
    } finally {
      setRetryingId(null);
    }
  };

  const handleDeleteFailed = async (id) => {
    if (!window.confirm('Permanently delete this failed application entry?')) return;
    setDeletingId(id);
    try {
      await dnoAPI.deleteFailedApplication(id);
      toast.success('Failed entry removed');
      setFailedDetail(null);
      fetchFailedApplications();
    } catch {
      toast.error('Could not delete entry');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await dnoAPI.syncStatuses();
      toast.success(`Synced ${res.data.updated} of ${res.data.total} applications`);
      fetchApplications();
    } catch {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const filteredApps = applications;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="dno-applications-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">DNO Applications</h1>
            <p className="text-slate-500 mt-1">ENA Connect Direct — G98/G99 Grid Connection Applications</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleSync} disabled={syncing} data-testid="sync-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Statuses'}
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setCreateOpen(true)} data-testid="new-application-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Application
            </Button>
          </div>
        </div>

        {!configured && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">ENA Connect Direct is not configured. Add API credentials in Settings.</p>
            </CardContent>
          </Card>
        )}

        {/* Surface the real load error so users know whether to fix the key or
            call support. Hides itself once a refresh succeeds. */}
        {loadError && (
          <Card className="border-rose-200 bg-rose-50" data-testid="dno-load-error">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-rose-900">
                  Couldn't load applications
                  {loadError.status ? <span className="ml-2 text-xs font-mono bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">HTTP {loadError.status}</span> : null}
                </p>
                <p className="text-sm text-rose-800 mt-0.5">{loadError.message}</p>
              </div>
              <Button size="sm" variant="outline" onClick={fetchApplications} className="flex-shrink-0">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry
              </Button>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="submitted" data-testid="tab-submitted">
              Submitted
              <Badge variant="secondary" className="ml-2 text-[10px] h-5">{applications.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="failed" data-testid="tab-failed">
              <XCircle className="w-3.5 h-3.5 mr-1 text-red-500" />
              Failed
              {failedApps.length > 0 && (
                <Badge className="ml-2 bg-red-100 text-red-700 border-red-200 text-[10px] h-5">{failedApps.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="submitted" className="space-y-4">
            {/* Filters */}
            <Card className="border-slate-200">
              <CardContent className="p-4">
            <div className="flex gap-4 items-center">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[250px]" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Auto-Approved">Auto-Approved</SelectItem>
                  <SelectItem value="DNO Approved">DNO Approved</SelectItem>
                  <SelectItem value="Escalated to DNO">Escalated to DNO</SelectItem>
                  <SelectItem value="Awaiting Assessment">Awaiting Assessment</SelectItem>
                  <SelectItem value="Under Assessment">Under Assessment</SelectItem>
                  <SelectItem value="Further Information Required">Further Info Required</SelectItem>
                  <SelectItem value="DNO Works Required">DNO Works Required</SelectItem>
                  <SelectItem value="Commissioning Submitted">Commissioning Submitted</SelectItem>
                  <SelectItem value="Commissioning Accepted">Commissioning Accepted</SelectItem>
                  <SelectItem value="Failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-slate-500">{filteredApps.length} application{filteredApps.length !== 1 ? 's' : ''}</span>
            </div>
          </CardContent>
        </Card>

        {/* Applications Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : filteredApps.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                <Zap className="w-16 h-16 mb-4" />
                <p className="text-lg font-medium">No applications found</p>
                <p className="text-sm mt-1">Create your first DNO application to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Application ID</TableHead>
                      <TableHead className="font-semibold">Customer</TableHead>
                      <TableHead className="font-semibold">Address</TableHead>
                      <TableHead className="font-semibold">LCTs</TableHead>
                      <TableHead className="font-semibold">DNO</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Created</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredApps.map((app) => {
                      const appId = app.applicationId || '';
                      const status = app.applicationStatus || app.status || '';
                      const details = app.installerCustomerDetails || {};
                      const lcts = app.lcts || [];
                      const created = app.createdDate || '';
                      const helios = app._helios || {};
                      return (
                        <TableRow
                          key={appId}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => setDetailApp(app)}
                          data-testid={`app-row-${appId}`}
                        >
                          <TableCell>
                            <span className="font-mono text-sm text-blue-700">{appId.slice(0, 12)}...</span>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-900">{helios.customer_name || details.customerName || '-'}</p>
                              <p className="text-xs text-slate-500">{details.customerEmail || ''}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm text-slate-700 truncate max-w-[200px]">{details.mainAddress || '-'}</p>
                              <p className="text-xs text-slate-500">{details.installationPostCode || ''}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              {lcts.map(l => (
                                <Badge key={l} variant="outline" className="text-xs">{LCT_LABELS[l] || l}</Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-700">{app.dno || '-'}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <StatusIcon status={status} />
                              <Badge className={`border text-xs ${STATUS_STYLES[status] || 'bg-slate-100 text-slate-700 border-slate-300'}`}>
                                {status || 'Unknown'}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-500">
                              {created ? new Date(created).toLocaleDateString('en-GB') : '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); setDetailApp(app); }}
                              className="h-8 text-xs text-blue-700 hover:bg-blue-50"
                              data-testid={`app-see-more-${appId}`}
                            >
                              <Eye className="w-3.5 h-3.5 mr-1" /> See more
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="failed" className="space-y-4">
            <Card className="border-slate-200">
              <CardContent className="p-0">
                {failedLoading ? (
                  <div className="flex items-center justify-center h-48">
                    <Loader2 className="w-8 h-8 animate-spin text-red-500" />
                  </div>
                ) : failedApps.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <CheckCircle2 className="w-16 h-16 mb-4 text-emerald-300" />
                    <p className="text-lg font-medium">No failed submissions</p>
                    <p className="text-sm mt-1">Submissions rejected by ENA will appear here so you can review or retry them.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold">Customer</TableHead>
                          <TableHead className="font-semibold">Address</TableHead>
                          <TableHead className="font-semibold">LCTs</TableHead>
                          <TableHead className="font-semibold">Error</TableHead>
                          <TableHead className="font-semibold">Attempted</TableHead>
                          <TableHead className="font-semibold">By</TableHead>
                          <TableHead className="w-[180px] text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {failedApps.map((f) => (
                          <TableRow
                            key={f.id}
                            className="hover:bg-red-50/30 cursor-pointer"
                            onClick={() => setFailedDetail(f)}
                            data-testid={`failed-app-row-${f.id}`}
                          >
                            <TableCell>
                              <p className="font-medium text-slate-900">{f.customer_name || '-'}</p>
                              <p className="text-xs text-slate-500">{f.customer_email || ''}</p>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm text-slate-700 truncate max-w-[200px]">{f.address || '-'}</p>
                              <p className="text-xs text-slate-500">{f.postcode || ''}</p>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1 flex-wrap">
                                {(f.lcts || []).map(l => (
                                  <Badge key={l} variant="outline" className="text-xs">{LCT_LABELS[l] || l}</Badge>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell>
                              <p className="text-xs text-red-700 max-w-[280px] line-clamp-2" title={f.error_body}>
                                {(f.error_body || '').slice(0, 200)}{(f.error_body || '').length > 200 ? '…' : ''}
                              </p>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-slate-500">
                                {f.attempted_at ? new Date(f.attempted_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-slate-600">{f.attempted_by || '-'}</span>
                            </TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                                  onClick={() => handleRetryFailed(f.id)}
                                  disabled={retryingId === f.id}
                                  data-testid={`retry-failed-${f.id}`}
                                >
                                  {retryingId === f.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <><RotateCw className="w-3 h-3 mr-1" />Retry</>
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs border-red-200 text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteFailed(f.id)}
                                  disabled={deletingId === f.id}
                                  data-testid={`delete-failed-${f.id}`}
                                >
                                  {deletingId === f.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Failed Application Detail Dialog */}
        <Dialog open={!!failedDetail} onOpenChange={(o) => !o && setFailedDetail(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-500" />
                Failed Submission — {failedDetail?.customer_name || 'Unknown customer'}
              </DialogTitle>
              <DialogDescription>
                Review the exact ENA response and the payload that was attempted. Fix the underlying customer data, then click Retry.
              </DialogDescription>
            </DialogHeader>
            {failedDetail && (
              <div className="space-y-3 max-h-[60vh] overflow-auto pr-1">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><span className="text-slate-500">Attempted:</span> {failedDetail.attempted_at && new Date(failedDetail.attempted_at).toLocaleString('en-GB')}</div>
                  <div><span className="text-slate-500">By:</span> {failedDetail.attempted_by || '-'}</div>
                  <div><span className="text-slate-500">Status:</span> {failedDetail.status_code || '-'}</div>
                  <div><span className="text-slate-500">LCTs:</span> {(failedDetail.lcts || []).join(', ') || '-'}</div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-red-700 mb-1">ENA error response</p>
                  <pre className="bg-red-50 border border-red-200 rounded p-3 text-[11px] text-red-900 whitespace-pre-wrap break-words max-h-[200px] overflow-auto">
{failedDetail.error_body || '(empty)'}
                  </pre>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-700 mb-1">Submitted payload</p>
                  <pre className="bg-slate-50 border border-slate-200 rounded p-3 text-[11px] text-slate-700 whitespace-pre-wrap break-words max-h-[260px] overflow-auto">
{JSON.stringify(failedDetail.payload || {}, null, 2)}
                  </pre>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                className="border-red-200 text-red-700 hover:bg-red-50"
                onClick={() => failedDetail && handleDeleteFailed(failedDetail.id)}
                disabled={deletingId === failedDetail?.id}
                data-testid="failed-detail-delete"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete entry
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => failedDetail && handleRetryFailed(failedDetail.id)}
                disabled={retryingId === failedDetail?.id}
                data-testid="failed-detail-retry"
              >
                {retryingId === failedDetail?.id ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Retrying…</>
                ) : (
                  <><RotateCw className="w-4 h-4 mr-2" />Retry submission</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Application Dialog */}
        <CreateApplicationDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          presetCustomerId={presetCustomerId}
          onCreated={() => { setCreateOpen(false); setPresetCustomerId(''); fetchApplications(); fetchFailedApplications(); }}
          onFailed={() => { fetchFailedApplications(); setActiveTab('failed'); }}
        />

        {/* Application Detail Dialog */}
        {detailApp && (
          <ApplicationDetailDialog
            app={detailApp}
            onClose={() => setDetailApp(null)}
          />
        )}
      </div>
    </Layout>
  );
}


function CreateApplicationDialog({ open, onOpenChange, onCreated, onFailed, presetCustomerId }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingProposal, setUploadingProposal] = useState(false);
  // Latest extracted MCS data — shown in a preview banner so the admin can
  // verify the fields Gemini filled before pressing Submit.
  const [extractedProposal, setExtractedProposal] = useState(null);
  const [applyingMcs, setApplyingMcs] = useState(false);

  // Form state
  const [lcts, setLcts] = useState(['SOLAR_PV']);
  const [form, setForm] = useState({
    customerName: '', customerEmail: '', customerPhone: '',
    mainAddress: '', installationPostCode: '', addressLine2: '',
    mpan: '', meterNumber: '',
    installerName: 'Helios Electrical & Solar', installerCompany: 'Helios Electrical & Solar',
    installerPhone: '01908 348250', installerEmail: 'DNO@helioses.co.uk',
    phaseCode: 1, declaredVoltage: '230 V', premisesCutOutRating: 60,
    isLoopedSupply: false, isNoIssues: true, isNoSafetyConcerns: true,
    isCtMetered: false, isExportLimitDevicePresent: false, isImportLimitingDevicePresent: false,
    newPremisesMaxDemand: 0,
    // ENA devicesToInstall required fields (editable per submission)
    targetInstallDate: '',
    powerFactor: 1.0,
    // Battery chemistry (only used when BATTERY is in lcts) — default Li-ion
    batteryChemistry: 'Storage - Electrochemical Classic Batteries -Lithium Ion (Li\u2013ion)',
    // Battery storage capacity (kWh) — admin override sent on BATTERY entries
    batteryCapacityKwh: '',
  });
  const [cutOutImageId, setCutOutImageId] = useState('');
  // Pick an existing customer image document as the cut-out image (instead of
  // re-uploading a photo already on file).
  const [docPickerOpen, setDocPickerOpen] = useState(false);
  const [imageDocs, setImageDocs] = useState([]);
  const [loadingImageDocs, setLoadingImageDocs] = useState(false);
  const [attachingDocId, setAttachingDocId] = useState('');
  // ---- Letter of Authority auto-attach ------------------------------------
  // When raising a DNO we now auto-attach the customer's most-recent SIGNED
  // LoA to the ENA `additionalAttachments` payload. The admin sees a status
  // banner on step 4 and can toggle the auto-attach off if the DNO doesn't
  // require it. Sample preview lives at GET /api/loa/sample for admins who
  // haven't generated one yet.
  const [signedLoa, setSignedLoa] = useState(null);       // {id, filename, signed_at, signed_by_name, dno_name, application_type}
  const [loadingLoa, setLoadingLoa] = useState(false);
  const [attachLoa, setAttachLoa] = useState(true);       // user toggle on step 4
  const [loaSampleOpen, setLoaSampleOpen] = useState(false);
  const [loaSampleBlobUrl, setLoaSampleBlobUrl] = useState('');
  const [loadingSample, setLoadingSample] = useState(false);
  // ---- Single Line Diagram (SLD) — required additional attachment for G99 -
  const [sldFileUrl, setSldFileUrl] = useState('');
  const [sldFilename, setSldFilename] = useState('');
  const [uploadingSld, setUploadingSld] = useState(false);
  const [sldDismissed, setSldDismissed] = useState(false); // admin acknowledged "not required"
  // ---- Site boundary map (optional additional attachment) ------------------
  const [boundaryFileUrl, setBoundaryFileUrl] = useState('');
  const [boundaryFilename, setBoundaryFilename] = useState('');
  const [boundaryDocId, setBoundaryDocId] = useState('');
  const [deviceSysRef, setDeviceSysRef] = useState('');
  const [deviceSearch, setDeviceSearch] = useState('');
  const [deviceResults, setDeviceResults] = useState([]);
  const [searchingDevices, setSearchingDevices] = useState(false);
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  // Pre-check with ENA — shows the applicationClass ENA will require for the
  // current LCT combination, BEFORE we submit. Avoids racking up Failed-tab
  // entries when the LCT mix changes and ENA's required class shifts.
  const [precheck, setPrecheck] = useState({ status: 'idle', data: null, error: null });

  // ENA Sub-Installer Username — shown in wizard header so admins can see at
  // a glance which staff member the DNO will be filed under. Editable inline.
  const [enaUser, setEnaUser] = useState(null); // {name, email, ena_sub_installer_username_effective, is_override}
  const [enaUserEditing, setEnaUserEditing] = useState(false);
  const [enaUserDraft, setEnaUserDraft] = useState('');
  const [enaUserSaving, setEnaUserSaving] = useState(false);
  useEffect(() => {
    if (open) {
      authAPI.getMyEnaUsername()
        .then(r => { setEnaUser(r.data); setEnaUserDraft(r.data.ena_sub_installer_username_override || ''); })
        .catch(() => {});
    }
  }, [open]);
  const saveEnaUser = async () => {
    setEnaUserSaving(true);
    try {
      const res = await authAPI.updateMyEnaUsername(enaUserDraft);
      setEnaUser(prev => ({
        ...(prev || {}),
        ena_sub_installer_username_override: res.data.ena_sub_installer_username,
        ena_sub_installer_username_effective: res.data.ena_sub_installer_username || (prev?.email || '').split('@')[0],
        is_override: !res.data.cleared,
      }));
      setEnaUserEditing(false);
      toast.success(res.data.cleared ? 'ENA username cleared — will use email default' : `ENA username saved: ${res.data.ena_sub_installer_username}`);
    } catch (e) {
      toast.error('Could not save ENA username');
    } finally {
      setEnaUserSaving(false);
    }
  };
  const runPrecheck = async () => {
    setPrecheck({ status: 'loading', data: null, error: null });
    try {
      const res = await dnoAPI.resolveApplicationClass(lcts);
      setPrecheck({ status: 'ok', data: res.data, error: null });
      toast.success(`ENA expects applicationClass: ${res.data.applicationClass} (v${res.data.applicationClassVersion})`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setPrecheck({ status: 'error', data: null, error: typeof detail === 'string' ? detail : JSON.stringify(detail) });
      toast.error(`Pre-check failed: ${typeof detail === 'string' ? detail.slice(0, 200) : 'unknown error'}`);
    }
  };
  // Reset pre-check whenever the LCT mix changes so users don't act on stale data
  useEffect(() => { setPrecheck({ status: 'idle', data: null, error: null }); }, [lcts]);

  useEffect(() => {
    if (open) {
      customersAPI.getAll({}).then(r => {
        const list = r.data || [];
        setCustomers(list);
        // If this dialog was opened via the "Raise DNO" deep-link, jump
        // straight to step 2 with the customer pre-selected — saves the
        // admin from re-searching for them on step 1.
        if (presetCustomerId) {
          const preset = list.find(c => c.id === presetCustomerId);
          if (preset) {
            selectCustomer(preset);
            setStep(2);
          }
        }
      }).catch(() => {});
      if (!presetCustomerId) {
        setStep(1);
        setSelectedCustomer(null);
      }
      setCutOutImageId('');
      setDeviceSysRef('');
      setSldFileUrl('');
      setSldFilename('');
      setSldDismissed(false);
      setAttachLoa(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetCustomerId]);

  const selectCustomer = (c) => {
    setSelectedCustomer(c);
    // Default install date: confirmed → provisional → today + 30 days
    const customerInstall = c.confirmed_install_date || c.provisional_install_date || '';
    const fallbackInstall = (() => {
      const d = new Date();
      d.setDate(d.getDate() + 30);
      return d.toISOString().slice(0, 10);
    })();
    setForm(prev => ({
      ...prev,
      customerName: `${c.first_name} ${c.last_name}`,
      customerEmail: c.email || '',
      customerPhone: c.phone || '',
      mainAddress: c.address || '',
      installationPostCode: c.postcode || '',
      addressLine2: c.city || '',
      targetInstallDate: prev.targetInstallDate || (customerInstall ? customerInstall.slice(0, 10) : fallbackInstall),
      // Pre-fill battery kWh from customer record if recorded — admin can still
      // override on step 4. Looks at the few common field names we've used.
      batteryCapacityKwh: prev.batteryCapacityKwh || c.battery_capacity_kwh || c.battery_size_kwh || c.battery_capacity || '',
    }));
    // Pull TTR device data from customer record
    if (c.inverter_device_ref && c.inverter_device_data) {
      setSelectedDevice(c.inverter_device_data);
      setDeviceSysRef(c.inverter_device_ref);
    }
    // Pull cut-out image if already uploaded on customer
    if (c.cutout_image_url) {
      setCutOutImageId(c.cutout_image_url);
    }
    // Prefill MPAN: try the ENA Connect Direct lookup first; if it doesn't
    // return one, fall back to the MPAN captured on the customer file.
    (async () => {
      let mpan = '';
      try {
        const { data } = await dnoAPI.mpanLookup({ address: c.address || '', postcode: c.postcode || '', uprn: c.uprn || '' });
        if (data?.found && data.mpan) mpan = data.mpan;
      } catch { /* ENA lookup best-effort */ }
      if (!mpan) mpan = c.mpan || '';
      if (mpan) setForm(prev => ({ ...prev, mpan: prev.mpan || mpan }));
    })();
  };

  const updateForm = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const toggleLct = (lct) => {
    setLcts(prev => prev.includes(lct) ? prev.filter(l => l !== lct) : [...prev, lct]);
  };

  // Upload a Solar PV proposal PDF and auto-fill DNO wizard fields. Sends
  // the file to the backend extractor (Gemini), then maps the returned MCS
  // table values onto the form. The PDF is also attached to the customer's
  // documents (under Folder = "Proposals") so the admin doesn't have to
  // upload it twice. Visible on Step 2 once a customer is selected.
  const handleApplyMcsToCustomer = async (overwrite = false) => {
    if (!selectedCustomer?.id) {
      toast.error('Select a customer first');
      return;
    }
    setApplyingMcs(true);
    try {
      const res = await api.post(
        `/customers/${selectedCustomer.id}/apply-mcs-data`,
        { overwrite },
      );
      const updated = res.data?.updated_fields || {};
      const skipped = res.data?.skipped || [];
      const count = Object.keys(updated).length;
      if (count === 0 && skipped.length > 0) {
        toast.info(
          `All ${skipped.length} fields already populated. Use "Force overwrite" to replace existing values.`,
          { duration: 6000 },
        );
      } else {
        const labels = Object.entries(updated)
          .filter(([k]) => k !== 'updated_at')
          .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
          .slice(0, 4)
          .join(' · ');
        toast.success(
          `Updated ${count} customer field${count === 1 ? '' : 's'} — ${labels}${count > 4 ? '…' : ''}`,
          { duration: 6000 },
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply data to customer record');
    } finally {
      setApplyingMcs(false);
    }
  };

  const handleUploadProposal = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please choose a PDF proposal file');
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      toast.error('Proposal PDF exceeds 30 MB');
      return;
    }
    setUploadingProposal(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // axios (api instance) auto-sets multipart boundary
      const url = selectedCustomer?.id
        ? `/dno/extract-from-proposal/start?customer_id=${encodeURIComponent(selectedCustomer.id)}`
        : `/dno/extract-from-proposal/start`;
      const result = await runProposalJob(api, url, fd);
      const data = result?.extracted || {};
      setExtractedProposal({ ...data, _attached: result?.attached });

      // Map extracted values onto wizard fields where they exist. Only
      // overwrite blank fields so we don't clobber what the admin has
      // already typed.
      setForm(prev => {
        const next = { ...prev };
        const setIfEmpty = (k, v) => {
          if (v == null || v === '') return;
          if (!prev[k]) next[k] = v;
        };
        // Customer block
        setIfEmpty('customerName', data.customer_name);
        setIfEmpty('mainAddress', data.customer_address);
        setIfEmpty('mpan', data.mpan);
        setIfEmpty('installationPostCode', data.postcode);
        // Battery capacity override — only sent on BATTERY devices
        if (data.battery_usable_capacity_kwh != null && !prev.batteryCapacityKwh) {
          next.batteryCapacityKwh = String(data.battery_usable_capacity_kwh);
        }
        return next;
      });

      // Adjust LCTs based on what's in the proposal
      const hasBattery = !!(data.battery_usable_capacity_kwh && data.battery_usable_capacity_kwh > 0);
      setLcts(prev => {
        const set = new Set(prev);
        if (data.installed_capacity_kwp) set.add('SOLAR_PV');
        if (hasBattery) set.add('BATTERY');
        return Array.from(set);
      });

      const filled = [];
      if (data.installed_capacity_kwp) filled.push(`${data.installed_capacity_kwp}kWp`);
      if (data.panel_count && data.panel_wattage_w) filled.push(`${data.panel_count}×${data.panel_wattage_w}W ${data.panel_manufacturer || 'panels'}`);
      if (data.battery_usable_capacity_kwh) filled.push(`${data.battery_usable_capacity_kwh}kWh battery`);
      if (data.inverter_model) filled.push(`${data.inverter_manufacturer || ''} ${data.inverter_model}`.trim());
      toast.success(
        result?.attached
          ? `Proposal attached & ${filled.length} fields read: ${filled.slice(0,3).join(' · ')}${filled.length>3?'…':''}`
          : `Read ${filled.length} fields: ${filled.slice(0,3).join(' · ')}${filled.length>3?'…':''}`,
        { duration: 5000 },
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Proposal extraction failed');
    } finally {
      setUploadingProposal(false);
      // reset input so the same file can be re-uploaded if extraction was off
      try { e.target.value = ''; } catch { /* ignore */ }
    }
  };

  const handleUploadCutOut = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);  try {
      const res = await dnoAPI.uploadAttachment(file, 'image');
      // ENA's response shape is inconsistent across DNO regions — try the
      // documented variants in priority order so we never lose the URL.
      const data = res.data || {};
      const url = data.fileUrl || data.file_url || data.url || data.attachmentUrl || data.attachment_url || '';
      if (!url) {
        // eslint-disable-next-line no-console
        console.error('Cut-out upload returned no URL — full response:', data);
        toast.error('Upload succeeded but no file URL returned. Please retry — see console for details.');
        return;
      }
      setCutOutImageId(url);
      toast.success('Cut-out image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  // Open the picker and load the customer's image documents.
  const openCutoutDocPicker = async () => {
    if (!selectedCustomer?.id) { toast.error('Select a customer first'); return; }
    setDocPickerOpen(true);
    setLoadingImageDocs(true);
    try {
      const { data } = await api.get(`/customers/${selectedCustomer.id}/dno/image-documents`);
      setImageDocs(data || []);
    } catch (e) {
      toast.error('Could not load customer images');
    } finally {
      setLoadingImageDocs(false);
    }
  };

  // Attach the chosen customer document to ENA and use it as the cut-out image.
  const chooseCutoutDoc = async (docId) => {
    setAttachingDocId(docId);
    try {
      const { data } = await api.post(`/customers/${selectedCustomer.id}/dno/attach-document-to-ena`, { document_id: docId });
      const url = data.file_url || data.fileUrl || '';
      if (!url) { toast.error('Attach succeeded but no file URL returned'); return; }
      setCutOutImageId(url);
      setDocPickerOpen(false);
      toast.success('Cut-out image attached from customer documents');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not attach document');
    } finally {
      setAttachingDocId('');
    }
  };

  // Upload the Single Line Diagram (SLD) — required additional attachment
  // for G99 submissions. Re-uses the ENA upload-attachment endpoint with
  // `file_type=file` (rather than image) so PDFs / DWG-exports are accepted.
  const handleUploadSld = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSld(true);
    try {
      const res = await dnoAPI.uploadAttachment(file, 'file');
      const data = res.data || {};
      const url = data.fileUrl || data.file_url || data.url || data.attachmentUrl || data.attachment_url || '';
      if (!url) {
        toast.error('SLD upload succeeded but no file URL returned. Please retry.');
        return;
      }
      setSldFileUrl(url);
      setSldFilename(file.name);
      toast.success(`Single Line Diagram uploaded: ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SLD upload failed');
    } finally {
      setUploadingSld(false);
      try { e.target.value = ''; } catch { /* ignore */ }
    }
  };

  // Whenever the wizard reaches step 4 or the customer changes, look up the
  // customer's most-recent signed LoA so we know whether to auto-attach it.
  useEffect(() => {
    if (!open || !selectedCustomer?.id) { setSignedLoa(null); return; }
    let cancelled = false;
    // Existing boundary map on file → pre-attach it (re-openable to update).
    setBoundaryFileUrl(''); setBoundaryFilename(''); setBoundaryDocId('');
    dnoAPI.getBoundaryMap(selectedCustomer.id)
      .then((r) => {
        if (cancelled || !r.data?.document) return;
        setBoundaryFilename(r.data.document.filename || 'Boundary Map.png');
        setBoundaryDocId(r.data.document.id || '');
        if (r.data.geometry?.ena_file_url) setBoundaryFileUrl(r.data.geometry.ena_file_url);
      })
      .catch(() => {});
    setLoadingLoa(true);
    api.get(`/customers/${selectedCustomer.id}/loa/signed`)
      .then(r => { if (!cancelled) setSignedLoa(r.data); })
      .catch((err) => {
        if (!cancelled) {
          setSignedLoa(null);
          if (err.response?.status && err.response.status !== 404) {
            // eslint-disable-next-line no-console
            console.warn('LoA lookup failed:', err);
          }
        }
      })
      .finally(() => { if (!cancelled) setLoadingLoa(false); });
    return () => { cancelled = true; };
  }, [open, selectedCustomer?.id]);

  // Fetch the sample LoA PDF as a blob (auth header is on api client) and
  // turn it into an object URL that the iframe can render. Done lazily the
  // first time the admin opens the preview dialog. If the customer already
  // has a signed LoA on file we reuse its DNO/application type so the
  // sample matches what we'd actually send.
  const openLoaSample = async () => {
    setLoaSampleOpen(true);
    if (loaSampleBlobUrl) return;
    setLoadingSample(true);
    try {
      const r = await api.get('/loa/sample', {
        params: {
          dno_name: signedLoa?.dno_name || 'UK Power Networks (UKPN)',
          application_type: signedLoa?.application_type || 'G99',
        },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      setLoaSampleBlobUrl(url);
    } catch (err) {
      toast.error('Could not load sample LoA');
      setLoaSampleOpen(false);
    } finally {
      setLoadingSample(false);
    }
  };

  // Open the signed LoA in a new tab — fetched as a blob through the auth'd
  // api client so the JWT header is carried, then re-opened via blob URL.
  const previewSignedLoa = async () => {
    if (!selectedCustomer?.id || !signedLoa?.id) return;
    try {
      const r = await api.get(
        `/customers/${selectedCustomer.id}/loa/${signedLoa.id}/download`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after a minute so memory frees but the tab can still load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error('Could not load signed LoA');
    }
  };

  // Release the sample blob URL when the dialog closes / unmounts
  useEffect(() => () => {
    if (loaSampleBlobUrl) URL.revokeObjectURL(loaSampleBlobUrl);
  }, [loaSampleBlobUrl]);


  const handleSubmit = async () => {
    if (!cutOutImageId) {
      toast.error('Please upload a cut-out image');
      return;
    }
    if (!selectedDevice && lcts.some(l => ['SOLAR_PV', 'BATTERY'].includes(l))) {
      toast.error('Please select a device from the ENA register');
      return;
    }
    if (!form.targetInstallDate) {
      toast.error('Please enter the target installation date');
      return;
    }
    if (!form.installerPhone) {
      toast.error('Installer phone is required');
      return;
    }
    if (!sldFileUrl && !sldDismissed) {
      toast.error('Please upload the Single Line Diagram (SLD), or tick "Not required for this application" if it doesn\'t apply.');
      return;
    }

    setSubmitting(true);

    // ---- Build the additionalAttachments list ----------------------------
    // 1. Auto-attach signed LoA (server-side: backend pulls our PDF and
    //    re-uploads to ENA, returning the file_url).
    // 2. SLD upload (already an ENA file_url at this point).
    const additionalAttachments = [];
    if (attachLoa && signedLoa && selectedCustomer?.id) {
      try {
        const r = await api.post(`/customers/${selectedCustomer.id}/loa/attach-to-ena`);
        const loaUrl = r.data?.file_url || r.data?.fileUrl;
        if (loaUrl) {
          additionalAttachments.push({
            fileUrl: loaUrl,
            description: `Letter of Authority (signed ${(signedLoa.signed_at || '').slice(0, 10)})`,
          });
        } else {
          toast.warning('LoA upload to ENA returned no URL — application will be submitted without it.');
        }
      } catch (err) {
        const msg = err.response?.data?.detail || err.message;
        toast.error(`Could not attach signed LoA: ${msg}. Submitting without it.`, { duration: 8000 });
      }
    }
    if (sldFileUrl) {
      additionalAttachments.push({
        fileUrl: sldFileUrl,
        description: `Single Line Diagram${sldFilename ? ` (${sldFilename})` : ''}`,
      });
    }
    if (boundaryFileUrl) {
      additionalAttachments.push({
        fileUrl: boundaryFileUrl,
        description: `Site boundary map with meter location${boundaryFilename ? ` (${boundaryFilename})` : ''}`,
      });
    }
    // Declare outside try so the catch block can read it for diagnostics
    let payload;
    try {
      // ENA's expected applicationClass varies by LCT combination. The /create
      // endpoint only accepts 3 literal classes (verified against ENA response
      // on 2026-05-14): GENERATION_GENERAL, DEMAND_GENERAL, GENERATION_DEMAND_GENERAL.
      // Battery is treated as a generation device — so PV / Battery / PV+Battery
      // all map to GENERATION_GENERAL.
      const isGeneration = lcts.some(l => ['SOLAR_PV', 'BATTERY'].includes(l));
      const isDemand = lcts.some(l => ['HP', 'EVCP_AC', 'EVCP_DC', 'EVV2G', 'ACU', 'EB'].includes(l));
      let appClass = 'GENERATION_GENERAL';
      if (isDemand && !isGeneration) {
        appClass = 'DEMAND_GENERAL';
      } else if (isDemand && isGeneration) {
        appClass = 'GENERATION_DEMAND_GENERAL';
      }

      // ENA per-device required fields (enum values verified against ENA API 2026-05-12).
      // - deviceClass is a single literal: GEN_DEVICE_GENERAL_ENA_REGISTERED
      // - generatorType: 'Photovoltaic' for PV, chemistry-specific for batteries
      const deviceDefaults = (lct) => {
        if (lct === 'BATTERY') {
          return {
            deviceClass: 'GEN_DEVICE_GENERAL_ENA_REGISTERED',
            generatorType: form.batteryChemistry || BATTERY_CHEMISTRIES[0],
            powerFactorDefault: 1.0,
          };
        }
        if (lct === 'SOLAR_PV') {
          return {
            deviceClass: 'GEN_DEVICE_GENERAL_ENA_REGISTERED',
            generatorType: 'Photovoltaic',
            powerFactorDefault: 0.95,
          };
        }
        return {
          deviceClass: 'GEN_DEVICE_GENERAL_ENA_REGISTERED',
          generatorType: 'Photovoltaic',
          powerFactorDefault: 1.0,
        };
      };

      const devices = [];
      if (selectedDevice) {
        for (const lct of lcts) {
          if (['SOLAR_PV', 'BATTERY'].includes(lct)) {
            const d = deviceDefaults(lct);
            const pf = form.powerFactor != null && form.powerFactor !== '' ? Number(form.powerFactor) : d.powerFactorDefault;
            const entry = {
              deviceType: lct === 'SOLAR_PV' ? 'PV' : 'Energy Storage Device',
              deviceClass: d.deviceClass,
              generatorType: d.generatorType,
              deviceSysRef: selectedDevice.DeviceRef,
              registeredCapacity: selectedDevice.RegisteredCapacity || 0,
              phase: String(selectedDevice.TTRDevicePhase || '1'),
              powerFactor: pf,
              targetInstallDate: form.targetInstallDate,
            };
            // Battery storage capacity (kWh) — admin-overridable on step 4.
            // Sent on BATTERY entries only when the user has provided a value.
            if (lct === 'BATTERY' && form.batteryCapacityKwh !== '' && form.batteryCapacityKwh != null) {
              const kwh = Number(form.batteryCapacityKwh);
              if (!Number.isNaN(kwh) && kwh > 0) {
                entry.energyCapacity = kwh;
                entry.energyCapacityUnit = 'kWh';
              }
            }
            devices.push(entry);
          }
        }
      }

      payload = {
        _customer_id: selectedCustomer?.id || null,
        applicationClass: appClass,
        applicationClassVersion: 1,
        lcts,
        cutOutImages: [{ fileUrl: cutOutImageId }],
        additionalAttachments,
        devicesToInstall: devices,
        existingDevices: [],
        installerCustomerDetails: {
          installerName: form.installerName,
          installerCompany: form.installerCompany,
          installerPhone: form.installerPhone,
          installerEmail: form.installerEmail,
          customerName: form.customerName,
          customerEmail: form.customerEmail,
          customerPhone: form.customerPhone,
          mainAddress: form.mainAddress,
          installationPostCode: form.installationPostCode,
          addressLine2: form.addressLine2,
          mpan: form.mpan || undefined,
          meterNumber: form.meterNumber || undefined,
        },
        supplyDetails: {
          declaredVoltageAtConnectionPoint: form.declaredVoltage,
          phaseCode: form.phaseCode,
          isCtMetered: form.isCtMetered,
          premisesCutOutRating: form.premisesCutOutRating,
          isLoopedSupply: form.isLoopedSupply,
          isNoIssues: form.isNoIssues,
          isNoSafetyConcerns: form.isNoSafetyConcerns,
          isExportLimitDevicePresent: form.isExportLimitDevicePresent,
          isImportLimitingDevicePresent: form.isImportLimitingDevicePresent,
          importLimitDeviceDetails: form.isImportLimitingDevicePresent ? (form.importLimitDeviceDetails || null) : null,
        },
        newPremisesMaxDemand: { newPremisesMaxDemand: form.newPremisesMaxDemand },
      };

      const res = await dnoAPI.createApplication(payload);
      toast.success(`Application submitted! ID: ${res.data?.applicationId?.slice(0, 12)}...`);
      onCreated();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      // ENA returns very long validation messages — log the full thing so we can see all 5+ errors
      // eslint-disable-next-line no-console
      console.error('DNO submission failed. Full ENA response:', err.response?.data, '\nPayload was:', payload);
      const detailStr = typeof detail === 'string' ? detail : JSON.stringify(detail);
      toast.error(`Submission failed — saved to "Failed" tab for retry. ${detailStr.slice(0, 300)}…`, {
        duration: 12000,
      });
      // Refresh the parent's Failed tab so the new entry shows immediately
      if (typeof onFailed === 'function') onFailed();
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCustomers = customerSearch
    ? customers.filter(c =>
        `${c.first_name} ${c.last_name} ${c.email} ${c.postcode}`.toLowerCase().includes(customerSearch.toLowerCase())
      ).slice(0, 10)
    : customers.slice(0, 10);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-600" />
            New DNO Application — Step {step}/4
          </DialogTitle>
          <DialogDescription>Submit a grid connection application via ENA Connect Direct</DialogDescription>

          {/* Filing-as badge: shows which staff member's ENA sub-installer username
              will be used on submit. Click pencil to override inline. */}
          {enaUser && (
            <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="dno-filing-as">
              <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-700 font-normal">
                Filing as: <strong className="ml-1 font-semibold">{enaUser.name || enaUser.email}</strong>
              </Badge>
              {enaUserEditing ? (
                <div className="flex items-center gap-1.5">
                  <Input
                    value={enaUserDraft}
                    onChange={e => setEnaUserDraft(e.target.value)}
                    placeholder="ENA username override"
                    className="h-7 text-xs w-44"
                    data-testid="dno-ena-username-input"
                  />
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-700" onClick={saveEnaUser} disabled={enaUserSaving} data-testid="dno-ena-username-save">
                    {enaUserSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-500" onClick={() => { setEnaUserEditing(false); setEnaUserDraft(enaUser.ena_sub_installer_username_override || ''); }} data-testid="dno-ena-username-cancel">
                    <XIcon className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setEnaUserEditing(true)}
                  className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 font-mono"
                  data-testid="dno-ena-username-edit"
                >
                  ENA: <span className="text-slate-700">{enaUser.ena_sub_installer_username_effective || '—'}</span>
                  {!enaUser.is_override && <span className="text-[10px] text-slate-400">(auto)</span>}
                  <Pencil className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-2">
            <Label className="text-base font-semibold">1. Select Customer</Label>
            <Input
              placeholder="Search customers..."
              value={customerSearch}
              onChange={e => setCustomerSearch(e.target.value)}
              data-testid="customer-search-dno"
            />
            <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
              {filteredCustomers.map(c => (
                <div
                  key={c.id}
                  className={`p-3 cursor-pointer hover:bg-blue-50 flex justify-between items-center ${selectedCustomer?.id === c.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                  onClick={() => selectCustomer(c)}
                  data-testid={`select-customer-${c.id}`}
                >
                  <div>
                    <p className="font-medium text-sm">{c.first_name} {c.last_name}</p>
                    <p className="text-xs text-slate-500">{c.address}, {c.postcode}</p>
                  </div>
                  {selectedCustomer?.id === c.id && <CheckCircle2 className="w-5 h-5 text-blue-600" />}
                </div>
              ))}
            </div>
            {selectedCustomer && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                Selected: <strong>{selectedCustomer.first_name} {selectedCustomer.last_name}</strong> — {selectedCustomer.address}, {selectedCustomer.postcode}
              </div>
            )}

            <Label className="text-base font-semibold mt-4">Technology Type</Label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(LCT_LABELS).map(([key, label]) => (
                <div
                  key={key}
                  className={`p-3 border rounded-lg cursor-pointer text-center text-sm transition-all ${lcts.includes(key) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white hover:bg-slate-50 border-slate-200'}`}
                  onClick={() => toggleLct(key)}
                  data-testid={`lct-${key}`}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Label className="text-base font-semibold">2. Customer & Installer Details</Label>
              {/* Auto-fill from a Solar PV proposal PDF. Lives at the top of
                  step 2 so the admin can run it once and skip a lot of
                  typing. Hidden until a customer is selected so we can
                  attach the PDF to the right record. */}
              {selectedCustomer && (
                <label
                  className={`inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border cursor-pointer transition-colors ${uploadingProposal ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-wait' : 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'}`}
                  data-testid="upload-proposal-pdf"
                >
                  {uploadingProposal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  <span>{uploadingProposal ? 'Reading proposal…' : 'Auto-fill from Proposal PDF'}</span>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    disabled={uploadingProposal}
                    onChange={handleUploadProposal}
                  />
                </label>
              )}
            </div>

            {extractedProposal && (
              <div className="border border-emerald-200 bg-emerald-50/60 rounded-lg p-3 text-xs space-y-1" data-testid="proposal-extract-preview">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="font-semibold text-emerald-800 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />
                    Extracted from proposal {extractedProposal._attached ? '· attached to customer documents' : ''}
                  </div>
                  {/* "Apply to Customer Record" — copies the extracted
                      equipment/system fields into the structured customer
                      record so quotations + MCS certs auto-populate. Never
                      overwrites existing values by default — click again
                      with Shift held (or use the menu inside CustomerDetail)
                      to force-overwrite. */}
                  {selectedCustomer && (
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={applyingMcs}
                        className="h-7 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                        onClick={(ev) => handleApplyMcsToCustomer(ev.shiftKey)}
                        title="Click to fill empty customer fields. Hold Shift to overwrite existing values."
                        data-testid="apply-mcs-to-customer-btn"
                      >
                        {applyingMcs ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                        Apply to Customer Record
                      </Button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-0.5 text-slate-700 mt-1">
                  {extractedProposal.installed_capacity_kwp != null && (
                    <div>System: <strong>{extractedProposal.installed_capacity_kwp} kWp</strong></div>
                  )}
                  {extractedProposal.panel_count != null && extractedProposal.panel_wattage_w != null && (
                    <div>Panels: <strong>{extractedProposal.panel_count} × {extractedProposal.panel_wattage_w}W</strong></div>
                  )}
                  {extractedProposal.panel_manufacturer && (
                    <div>Make: <strong>{extractedProposal.panel_manufacturer}</strong></div>
                  )}
                  {extractedProposal.battery_usable_capacity_kwh != null && (
                    <div>Battery: <strong>{extractedProposal.battery_usable_capacity_kwh} kWh</strong></div>
                  )}
                  {extractedProposal.battery_manufacturer && (
                    <div>Battery make: <strong>{extractedProposal.battery_manufacturer}</strong></div>
                  )}
                  {extractedProposal.inverter_model && (
                    <div>Inverter: <strong>{extractedProposal.inverter_manufacturer || ''} {extractedProposal.inverter_model}</strong></div>
                  )}
                  {extractedProposal.inclination_degrees_from_horizontal != null && (
                    <div>Pitch: <strong>{extractedProposal.inclination_degrees_from_horizontal}°</strong></div>
                  )}
                  {extractedProposal.orientation_degrees_from_south != null && (
                    <div>Orientation: <strong>{extractedProposal.orientation_degrees_from_south}° from S</strong></div>
                  )}
                  {extractedProposal.estimated_annual_output_kwh != null && (
                    <div>Annual gen: <strong>{extractedProposal.estimated_annual_output_kwh} kWh</strong></div>
                  )}
                  {extractedProposal.annual_electricity_consumption_kwh != null && (
                    <div>Annual use: <strong>{extractedProposal.annual_electricity_consumption_kwh} kWh</strong></div>
                  )}
                  {extractedProposal.grid_independence_with_eess_percent != null && (
                    <div>Grid indep.: <strong>{extractedProposal.grid_independence_with_eess_percent}%</strong></div>
                  )}
                  {extractedProposal.postcode && (
                    <div>Postcode: <strong>{extractedProposal.postcode}</strong></div>
                  )}
                </div>
                <div className="pt-1 text-slate-500 italic">
                  Fields that were already filled are kept — review and edit any of the auto-filled values below.
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Customer Name</Label><Input value={form.customerName} onChange={e => updateForm('customerName', e.target.value)} data-testid="customer-name" /></div>
              <div><Label>Customer Email</Label><Input value={form.customerEmail} onChange={e => updateForm('customerEmail', e.target.value)} data-testid="customer-email" /></div>
              <div><Label>Customer Phone</Label><Input value={form.customerPhone} onChange={e => updateForm('customerPhone', e.target.value)} /></div>
              <div><Label>MPAN (optional)</Label><Input value={form.mpan} onChange={e => updateForm('mpan', e.target.value)} placeholder="Leave blank for auto-lookup" /></div>
              <div className="col-span-2"><Label>Installation Address</Label>
                <AddressAutocomplete
                  value={form.mainAddress}
                  onChange={(v) => updateForm('mainAddress', v)}
                  onSelect={(s) => {
                    updateForm('mainAddress', s.address_line_1 || s.label);
                    if (s.city) updateForm('addressLine2', s.city);
                    if (s.postcode) updateForm('installationPostCode', s.postcode);
                  }}
                  placeholder="Start typing the installation address…"
                  testId="main-address"
                />
              </div>
              <div><Label>Address Line 2</Label><Input value={form.addressLine2} onChange={e => updateForm('addressLine2', e.target.value)} /></div>
              <div><Label>Postcode</Label><Input value={form.installationPostCode} onChange={e => updateForm('installationPostCode', e.target.value)} data-testid="postcode" /></div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 py-2">
            <Label className="text-base font-semibold">3. Property Supply Details</Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Phase</Label>
                <Select value={String(form.phaseCode)} onValueChange={v => updateForm('phaseCode', Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Single Phase</SelectItem>
                    <SelectItem value="3">Three Phase</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Voltage at Connection</Label>
                <Select value={form.declaredVoltage} onValueChange={v => updateForm('declaredVoltage', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="230 V">230 V (Single Phase)</SelectItem>
                    <SelectItem value="400 V">400 V (Three Phase)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cut-Out Fuse Rating (A)</Label>
                <Select value={String(form.premisesCutOutRating)} onValueChange={v => updateForm('premisesCutOutRating', Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="60">60A</SelectItem>
                    <SelectItem value="80">80A</SelectItem>
                    <SelectItem value="100">100A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>New Max Demand (A)</Label>
                <Input type="number" value={form.newPremisesMaxDemand} onChange={e => updateForm('newPremisesMaxDemand', Number(e.target.value))} />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2"><Checkbox checked={form.isLoopedSupply} onCheckedChange={v => updateForm('isLoopedSupply', v)} /><Label>Looped Supply</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={form.isNoIssues} onCheckedChange={v => updateForm('isNoIssues', v)} /><Label>No known issues with supply</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={form.isNoSafetyConcerns} onCheckedChange={v => updateForm('isNoSafetyConcerns', v)} /><Label>No safety concerns</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={form.isExportLimitDevicePresent} onCheckedChange={v => updateForm('isExportLimitDevicePresent', v)} /><Label>Export Limit Device Present</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={form.isImportLimitingDevicePresent} onCheckedChange={v => updateForm('isImportLimitingDevicePresent', v)} data-testid="import-limit-device-present" /><Label>Import Limiting Device Present</Label></div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4 py-2">
            <Label className="text-base font-semibold">4. Devices & Attachments</Label>

            {/* ENA Pre-check — verifies the applicationClass before submission */}
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-2" data-testid="ena-precheck-block">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-indigo-900 flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5" /> ENA Pre-check
                  </p>
                  <p className="text-[11px] text-indigo-700/80">
                    Ask ENA what <code className="font-mono bg-white/60 px-1 rounded">applicationClass</code> it expects for this LCT mix before submitting.
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-100 flex-shrink-0"
                  onClick={runPrecheck}
                  disabled={precheck.status === 'loading' || !lcts.length}
                  data-testid="ena-precheck-btn"
                >
                  {precheck.status === 'loading' ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Search className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Run pre-check
                </Button>
              </div>
              {precheck.status === 'ok' && precheck.data && (
                <div className="space-y-1.5" data-testid="ena-precheck-ok">
                  <div className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>
                      ENA expects <strong className="font-mono">{precheck.data.applicationClass}</strong> (v{precheck.data.applicationClassVersion}) — the backend will use this on submit and auto-fill any missing required fields with safe defaults.
                    </span>
                  </div>
                  {(precheck.data.required_paths || []).length > 0 && (
                    <details className="text-xs text-slate-600">
                      <summary className="cursor-pointer hover:text-slate-900">
                        ENA requires {precheck.data.required_paths.length} field{precheck.data.required_paths.length === 1 ? '' : 's'} (click to view)
                      </summary>
                      <div className="mt-1.5 grid grid-cols-2 gap-x-2 gap-y-0.5 pl-3 font-mono text-[10px] text-slate-500">
                        {precheck.data.required_paths.map((p) => (
                          <span key={p}>· {p}</span>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
              {precheck.status === 'error' && (
                <div className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="ena-precheck-err">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>
                    Could not resolve via ENA schema: <span className="font-mono">{(precheck.error || '').slice(0, 200)}</span>
                    <br />
                    Submission will still proceed with the frontend default — check the Failed tab if it errors out.
                  </span>
                </div>
              )}
            </div>

            {lcts.some(l => ['SOLAR_PV', 'BATTERY'].includes(l)) && (
              <div className="space-y-2">
                <Label>Generation Device (from ENA Type Test Register)</Label>
                <Button
                  variant="outline"
                  className="w-full justify-between"
                  onClick={() => setDeviceDialogOpen(true)}
                  data-testid="open-device-search"
                >
                  {selectedDevice ? (
                    <span className="text-left truncate">
                      <span className="font-medium">{selectedDevice.TTRManufacturer}</span> — {selectedDevice.ModelNo}
                      <span className="text-slate-500 ml-2">{selectedDevice.RegisteredCapacity}kW</span>
                    </span>
                  ) : (
                    <span className="text-slate-500">Search & select inverter / device...</span>
                  )}
                  <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
                </Button>
                {selectedDevice && (
                  <div className="p-2 bg-emerald-50 rounded text-sm text-emerald-800 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    <span><strong className="font-mono">{selectedDevice.DeviceRef}</strong> — {selectedDevice.TTRManufacturer} {selectedDevice.ModelNo} ({selectedDevice.RegisteredCapacity}kW, {selectedDevice.TTRDevicePhase}ph)</span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div>
                    <Label htmlFor="dno-target-install-date">Target Installation Date *</Label>
                    <Input
                      id="dno-target-install-date"
                      type="date"
                      value={form.targetInstallDate}
                      onChange={e => updateForm('targetInstallDate', e.target.value)}
                      data-testid="dno-target-install-date"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">Pre-filled from the customer&apos;s confirmed install date.</p>
                  </div>
                  <div>
                    <Label htmlFor="dno-power-factor">Power Factor *</Label>
                    <Input
                      id="dno-power-factor"
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={form.powerFactor}
                      onChange={e => updateForm('powerFactor', e.target.value)}
                      data-testid="dno-power-factor"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">1.0 for batteries, 0.95 for PV inverters.</p>
                  </div>
                </div>
                {lcts.includes('BATTERY') && (
                  <div className="pt-2 space-y-3">
                    <div>
                      <Label htmlFor="dno-battery-chemistry">Battery Chemistry *</Label>
                      <Select
                        value={form.batteryChemistry}
                        onValueChange={(v) => updateForm('batteryChemistry', v)}
                      >
                        <SelectTrigger id="dno-battery-chemistry" data-testid="dno-battery-chemistry">
                          <SelectValue placeholder="Select battery chemistry" />
                        </SelectTrigger>
                        <SelectContent>
                          {BATTERY_CHEMISTRIES.map((c) => (
                            <SelectItem key={c} value={c}>{c.replace('Storage - Electrochemical ', '')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-slate-500 mt-1">99% of modern home batteries are Lithium Ion. Only change if your battery uses a different chemistry.</p>
                    </div>
                    <div>
                      <Label htmlFor="dno-battery-kwh">Battery Capacity Override (kWh)</Label>
                      <Input
                        id="dno-battery-kwh"
                        type="number"
                        step="0.1"
                        min="0"
                        value={form.batteryCapacityKwh}
                        onChange={(e) => updateForm('batteryCapacityKwh', e.target.value)}
                        placeholder="e.g. 13.5"
                        data-testid="dno-battery-kwh"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">
                        Storage capacity in kilowatt-hours. Leave blank to use the ENA-registered device default. Override here if multiple batteries are stacked or the customer has chosen a non-default size.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Cut-Out Image (required)</Label>
              {cutOutImageId ? (
                <div className="flex items-start gap-4 p-3 border border-emerald-200 bg-emerald-50 rounded-lg">
                  <img
                    src={cutOutImageId}
                    alt="Cut-out preview"
                    className="w-[200px] h-[200px] object-cover rounded-md border border-emerald-300 bg-white flex-shrink-0"
                    style={{ width: 200, height: 200 }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                    data-testid="cutout-thumbnail"
                  />
                  <div
                    className="w-[200px] h-[200px] border-2 border-dashed border-emerald-300 rounded-md bg-white items-center justify-center text-xs text-emerald-700 px-3 text-center hidden"
                    style={{ width: 200, height: 200 }}
                  >
                    Image uploaded — preview unavailable
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium text-sm">Cut-out image uploaded</span>
                    </div>
                    <p className="text-xs text-slate-600 break-all">{cutOutImageId}</p>
                    <div className="flex items-center gap-2 pt-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleUploadCutOut}
                        className="hidden"
                        id="cutout-replace-input"
                        data-testid="cutout-replace-input"
                      />
                      <label
                        htmlFor="cutout-replace-input"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-300 bg-white rounded cursor-pointer hover:bg-slate-50 transition-colors"
                        data-testid="cutout-replace-btn"
                      >
                        {uploadingImage ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        {uploadingImage ? 'Uploading…' : 'Replace'}
                      </label>
                      <button
                        type="button"
                        onClick={openCutoutDocPicker}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-300 bg-white rounded cursor-pointer hover:bg-slate-50 transition-colors"
                        data-testid="cutout-choose-doc-btn"
                      >
                        <FileText className="w-3 h-3" /> Choose from documents
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-3 p-3 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50">
                  <input type="file" accept="image/*" onChange={handleUploadCutOut} className="text-sm" data-testid="cutout-upload" />
                  {uploadingImage && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span className="text-xs text-slate-400">or</span>
                  <button
                    type="button"
                    onClick={openCutoutDocPicker}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-300 bg-white rounded cursor-pointer hover:bg-slate-50 transition-colors"
                    data-testid="cutout-choose-doc-btn-empty"
                  >
                    <FileText className="w-3 h-3" /> Choose from customer documents
                  </button>
                </div>
              )}
            </div>

            {/* ===== Additional Attachments: Letter of Authority + SLD ===== */}
            <div className="space-y-3 pt-2" data-testid="dno-additional-attachments">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Additional Attachments</Label>
                <span className="text-[11px] text-slate-500">Sent to ENA alongside the cut-out image</span>
              </div>

              {/* --- Letter of Authority status card ----------------------- */}
              {loadingLoa ? (
                <div className="p-3 border border-slate-200 bg-slate-50 rounded-lg flex items-center gap-2 text-sm text-slate-600">
                  <Loader2 className="w-4 h-4 animate-spin" /> Checking for a signed Letter of Authority…
                </div>
              ) : signedLoa ? (
                <div className="p-3 border border-emerald-200 bg-emerald-50 rounded-lg" data-testid="loa-attached-card">
                  <div className="flex items-start gap-3">
                    <FilePen className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-emerald-900">Signed Letter of Authority on file</span>
                        <Badge className="bg-emerald-600 text-white text-[10px]">Auto-attach</Badge>
                      </div>
                      <p className="text-xs text-emerald-800 mt-1">
                        Signed {signedLoa.signed_at ? new Date(signedLoa.signed_at).toLocaleDateString('en-GB') : '—'}
                        {signedLoa.signed_by_name ? ` by ${signedLoa.signed_by_name}` : ''}
                        {signedLoa.dno_name ? ` · ${signedLoa.dno_name}` : ''}
                        {signedLoa.application_type ? ` · ${signedLoa.application_type}` : ''}
                      </p>
                      <p className="text-[11px] text-emerald-700 mt-1">
                        Will be auto-uploaded to ENA as an additional attachment when you press Submit.
                      </p>
                      <div className="flex items-center gap-3 pt-2 flex-wrap">
                        <button
                          type="button"
                          onClick={previewSignedLoa}
                          className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 hover:underline"
                          data-testid="loa-preview-link"
                        >
                          <Eye className="w-3 h-3" /> Preview signed LoA
                        </button>
                        <button
                          type="button"
                          onClick={openLoaSample}
                          className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 hover:underline"
                          data-testid="loa-sample-link"
                        >
                          <FileText className="w-3 h-3" /> View blank LoA template
                        </button>
                        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer text-slate-700" data-testid="loa-attach-toggle">
                          <Checkbox checked={attachLoa} onCheckedChange={(v) => setAttachLoa(!!v)} />
                          Auto-attach to this submission
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-3 border border-amber-300 bg-amber-50 rounded-lg" data-testid="loa-missing-card">
                  <div className="flex items-start gap-3">
                    <FileWarning className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-900">No signed Letter of Authority on file</p>
                      <p className="text-xs text-amber-800 mt-1">
                        Generate and send the LoA to the customer for signature before raising the DNO — DNOs (e.g. UKPN, Western Power) require an LoA when an installer applies on a homeowner's behalf.
                      </p>
                      <div className="flex items-center gap-3 pt-2 flex-wrap">
                        {selectedCustomer?.id && (
                          <a
                            href={`/customers/${selectedCustomer.id}?openLoa=1`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-amber-700 text-white rounded hover:bg-amber-800"
                            data-testid="loa-generate-link"
                          >
                            <FilePen className="w-3 h-3" /> Open customer to generate LoA
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={openLoaSample}
                          className="inline-flex items-center gap-1 text-xs text-amber-800 hover:text-amber-900 hover:underline"
                          data-testid="loa-sample-link-missing"
                        >
                          <Eye className="w-3 h-3" /> See sample LoA & signing location
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* --- SLD upload reminder ----------------------------------- */}
              <div
                className={`p-3 border rounded-lg ${sldFileUrl ? 'border-emerald-200 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}
                data-testid="sld-reminder-card"
              >
                <div className="flex items-start gap-3">
                  {sldFileUrl ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Zap className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    {sldFileUrl ? (
                      <>
                        <p className="text-sm font-medium text-emerald-900">Single Line Diagram (SLD) attached</p>
                        <p className="text-xs text-emerald-800 mt-0.5 break-all">{sldFilename || sldFileUrl}</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-amber-900">Don't forget the Single Line Diagram (SLD)</p>
                        <p className="text-xs text-amber-800 mt-0.5">
                          Required by most DNOs for G99 applications — typically a PDF showing inverter(s), battery, isolation switches, CT clamp and grid connection point.
                        </p>
                      </>
                    )}
                    <div className="flex items-center gap-3 pt-2 flex-wrap">
                      <input
                        type="file"
                        accept="application/pdf,image/*,.dwg,.dxf"
                        onChange={handleUploadSld}
                        className="hidden"
                        id="sld-upload-input"
                        data-testid="sld-upload-input"
                      />
                      <label
                        htmlFor="sld-upload-input"
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded cursor-pointer transition-colors ${sldFileUrl ? 'border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50' : 'bg-amber-700 text-white hover:bg-amber-800'}`}
                        data-testid="sld-upload-btn"
                      >
                        {uploadingSld ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        {uploadingSld ? 'Uploading…' : sldFileUrl ? 'Replace SLD' : 'Upload SLD'}
                      </label>
                      <SldGenerateDialog
                        customer={selectedCustomer}
                        phaseCode={form.phaseCode}
                        cutoutRating={form.premisesCutOutRating}
                        onGenerated={(fileUrl, filename) => {
                          if (fileUrl) setSldFileUrl(fileUrl);
                          setSldFilename(filename || 'SLD.pdf');
                          setSldDismissed(false);
                        }}
                      />
                      <BoundaryMapDialog
                        customer={selectedCustomer}
                        onSaved={(fileUrl, filename, docId) => {
                          setBoundaryFileUrl(fileUrl || '');
                          setBoundaryFilename(filename || 'Boundary Map.png');
                          setBoundaryDocId(docId || '');
                        }}
                      />
                      {!sldFileUrl && (
                        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer text-amber-900" data-testid="sld-dismiss-toggle">
                          <Checkbox checked={sldDismissed} onCheckedChange={(v) => setSldDismissed(!!v)} />
                          Not required for this application
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* --- Boundary map status (optional) ------------------------ */}
              {boundaryFilename && (
                <div className="p-3 border border-emerald-200 bg-emerald-50 rounded-lg flex items-start gap-3" data-testid="boundary-map-attached-card">
                  <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-emerald-900">Site boundary map on file</p>
                    <p className="text-xs text-emerald-800 mt-0.5 break-all">
                      {boundaryFilename}
                      {boundaryFileUrl ? ' · will be attached to this submission' : ' · saved to the customer file (ENA attachment link unavailable — re-save once ENA is configured)'}
                    </p>
                    {boundaryDocId && selectedCustomer?.id && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${selectedCustomer.id}/${boundaryDocId}`} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 hover:underline mt-1" data-testid="boundary-map-preview-link">
                        <Eye className="w-3 h-3" /> View saved map
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 bg-slate-50 rounded-lg text-sm space-y-1">
              <p className="font-semibold">Summary</p>
              <p>Customer: {form.customerName || '—'}</p>
              <p>Address: {form.mainAddress}, {form.installationPostCode}</p>
              <p>Technology: {lcts.map(l => LCT_LABELS[l]).join(', ')}</p>
              <p>Phase: {form.phaseCode === 1 ? 'Single' : 'Three'} | Cut-out: {form.premisesCutOutRating}A</p>
              {selectedDevice && <p>Device: {selectedDevice.TTRManufacturer} {selectedDevice.ModelNo} ({selectedDevice.DeviceRef})</p>}
              <p className="text-xs text-slate-600 mt-1">
                Attachments: cut-out image
                {attachLoa && signedLoa ? ' · signed LoA (auto)' : ''}
                {sldFileUrl ? ' · SLD' : ''}
                {boundaryFileUrl ? ' · boundary map' : ''}
              </p>
            </div>
          </div>
        )}

        {/* LoA Sample Preview Dialog — admins can see what the auto-generated
            LoA looks like and the exact location where the customer's
            signature is captured. */}
        {/* Cut-out image picker — choose an existing customer image document */}
        <Dialog open={docPickerOpen} onOpenChange={setDocPickerOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="cutout-doc-picker">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-700" />
                Choose cut-out image from customer documents
              </DialogTitle>
              <DialogDescription>
                Pick an image already uploaded to this customer&apos;s file. It will be sent to ENA and used as the cut-out image for this application.
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              {loadingImageDocs ? (
                <div className="flex items-center justify-center py-10 text-slate-600 gap-2 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading customer images…
                </div>
              ) : imageDocs.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-500" data-testid="cutout-doc-picker-empty">
                  No image documents found on this customer&apos;s file. Upload a photo using the file picker instead.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-1">
                  {imageDocs.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => chooseCutoutDoc(d.id)}
                      disabled={!!attachingDocId}
                      className="group border border-slate-200 rounded-lg overflow-hidden bg-white hover:border-emerald-400 hover:shadow transition-all text-left disabled:opacity-60"
                      data-testid={`cutout-doc-${d.id}`}
                    >
                      <div className="w-full h-28 bg-slate-100 flex items-center justify-center overflow-hidden">
                        <img src={d.preview_url} alt={d.filename} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      </div>
                      <div className="p-2">
                        <p className="text-xs font-medium text-slate-700 truncate">{d.filename}</p>
                        {d.folder ? <p className="text-[10px] text-slate-400 truncate">{d.folder}</p> : null}
                        {attachingDocId === d.id && (
                          <p className="text-[10px] text-emerald-600 flex items-center gap-1 mt-1"><Loader2 className="w-3 h-3 animate-spin" /> Attaching…</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>


        <Dialog open={loaSampleOpen} onOpenChange={setLoaSampleOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="loa-sample-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-700" />
                Sample Letter of Authority
              </DialogTitle>
              <DialogDescription>
                This is what the auto-generated LoA looks like when sent to the customer. The customer signs it via a tokenised public link — the signature is captured on the line under <span className="font-medium">Sincerely</span>, then stamped onto the PDF and re-attached to the customer record. When raising the DNO, that signed PDF is auto-uploaded to ENA as an additional attachment.
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-hidden border rounded-lg bg-slate-100 relative">
              {loadingSample && (
                <div className="absolute inset-0 flex items-center justify-center text-slate-600 text-sm gap-2 z-10 bg-white/60">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading sample…
                </div>
              )}
              {loaSampleBlobUrl ? (
                <iframe
                  src={loaSampleBlobUrl}
                  title="Sample Letter of Authority"
                  className="w-full h-[65vh]"
                  data-testid="loa-sample-iframe"
                />
              ) : (
                <div className="w-full h-[65vh]" />
              )}
            </div>
            <DialogFooter className="pt-3">
              <Button variant="outline" onClick={() => setLoaSampleOpen(false)} data-testid="loa-sample-close">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Device Search Pop-out Dialog */}
        <DeviceSearchDialog
          open={deviceDialogOpen}
          onOpenChange={setDeviceDialogOpen}
          onSelect={(device) => {
            setSelectedDevice(device);
            setDeviceSysRef(device.DeviceRef);
            setDeviceDialogOpen(false);
          }}
          currentSelection={selectedDevice}
        />

        <DialogFooter className="flex justify-between">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(s => s - 1)}>Back</Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            {step < 4 ? (
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setStep(s => s + 1)} data-testid="next-step-btn">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleSubmit} disabled={submitting} data-testid="submit-application-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Submit to ENA
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function DeviceSearchDialog({ open, onOpenChange, onSelect, currentSelection }) {
  const [manufacturers, setManufacturers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [phases, setPhases] = useState([]);
  const [loadingRef, setLoadingRef] = useState(false);

  const [mfrFilter, setMfrFilter] = useState('');
  const [mfrSearch, setMfrSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(0);

  // Load reference data on open
  useEffect(() => {
    if (open && manufacturers.length === 0) {
      setLoadingRef(true);
      dnoAPI.getDeviceReference().then(res => {
        const data = res.data || {};
        setManufacturers((data.device_manufacturers || []).sort((a, b) => a.Manufacturer.localeCompare(b.Manufacturer)));
        setCategories(data.device_categories || []);
        setDeviceTypes(data.device_types || []);
        setPhases(data.device_phases || []);
      }).catch(() => {
        toast.error('Failed to load device reference data');
      }).finally(() => setLoadingRef(false));
    }
  }, [open, manufacturers.length]);

  const doSearch = async (skip = 0) => {
    if (!mfrFilter && !modelSearch) {
      toast.error('Please select a manufacturer or enter a model search');
      return;
    }
    setSearching(true);
    try {
      const params = { limit: 25, skip };
      if (mfrFilter) params.manufacturer_id = mfrFilter;
      if (catFilter) params.category_id = catFilter;
      if (typeFilter) params.device_type_id = typeFilter;
      if (phaseFilter) params.phase_id = phaseFilter;
      if (modelSearch) params.query = modelSearch;
      const res = await dnoAPI.searchDevices(params);
      setResults(res.data?.data || []);
      setTotalResults(res.data?.total_results || 0);
      setPage(skip);
    } catch {
      toast.error('Device search failed');
    } finally {
      setSearching(false);
    }
  };

  const filteredMfrs = mfrSearch
    ? manufacturers.filter(m => m.Manufacturer.toLowerCase().includes(mfrSearch.toLowerCase())).slice(0, 50)
    : manufacturers.slice(0, 50);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-blue-600" />
            ENA Type Test Register — Device Search
          </DialogTitle>
          <DialogDescription>
            Search {totalResults > 0 ? `${totalResults.toLocaleString()} devices` : '8,000+ registered devices'} on the ENA register
          </DialogDescription>
        </DialogHeader>

        {loadingRef ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            <span className="ml-3 text-slate-500">Loading register data...</span>
          </div>
        ) : (
          <div className="flex flex-col gap-4 flex-1 overflow-hidden">
            {/* Filters */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs text-slate-500 mb-1">Manufacturer</Label>
                <div className="relative">
                  <Input
                    placeholder="Type to filter manufacturers..."
                    value={mfrSearch}
                    onChange={e => setMfrSearch(e.target.value)}
                    data-testid="mfr-search-input"
                  />
                </div>
                {(mfrSearch || !mfrFilter) && filteredMfrs.length > 0 && (
                  <div className="max-h-32 overflow-y-auto border rounded-lg mt-1 divide-y text-sm bg-white">
                    {filteredMfrs.map(m => (
                      <div
                        key={m.ManufacturerID}
                        className={`px-3 py-1.5 cursor-pointer hover:bg-blue-50 ${String(mfrFilter) === String(m.ManufacturerID) ? 'bg-blue-100 font-medium' : ''}`}
                        onClick={() => { setMfrFilter(String(m.ManufacturerID)); setMfrSearch(m.Manufacturer); }}
                        data-testid={`mfr-option-${m.ManufacturerID}`}
                      >
                        {m.Manufacturer}
                      </div>
                    ))}
                  </div>
                )}
                {mfrFilter && (
                  <Button variant="ghost" size="sm" className="text-xs mt-1 h-6 px-2" onClick={() => { setMfrFilter(''); setMfrSearch(''); }}>
                    Clear manufacturer
                  </Button>
                )}
              </div>

              <div>
                <Label className="text-xs text-slate-500 mb-1">Model Number</Label>
                <Input
                  placeholder="e.g. H1-5.0"
                  value={modelSearch}
                  onChange={e => setModelSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && doSearch(0)}
                  data-testid="model-search-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1">Device Type</Label>
                <Select value={typeFilter || 'all'} onValueChange={v => setTypeFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {deviceTypes.map(t => (
                      <SelectItem key={t.DeviceTypeID} value={String(t.DeviceTypeID)}>{t.DeviceType}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button onClick={() => doSearch(0)} disabled={searching} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="search-devices-btn">
              {searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Search Devices
            </Button>

            {/* Results */}
            <div className="flex-1 overflow-y-auto border rounded-lg">
              {results.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                  <Zap className="w-10 h-10 mb-2" />
                  <p className="text-sm">{searching ? 'Searching...' : 'Select a manufacturer and search'}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 text-xs">
                      <TableHead>Manufacturer</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Capacity</TableHead>
                      <TableHead>Phase</TableHead>
                      <TableHead>Ref</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map(d => (
                      <TableRow
                        key={d.DeviceID}
                        className={`cursor-pointer hover:bg-blue-50 text-sm ${currentSelection?.DeviceID === d.DeviceID ? 'bg-blue-50' : ''}`}
                        onClick={() => onSelect(d)}
                        data-testid={`device-row-${d.DeviceID}`}
                      >
                        <TableCell className="font-medium">{d.TTRManufacturer}</TableCell>
                        <TableCell>{d.ModelNo}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{d.TTRDeviceType}</Badge></TableCell>
                        <TableCell>{d.RegisteredCapacity} kW</TableCell>
                        <TableCell>{d.TTRDevicePhase}ph</TableCell>
                        <TableCell><span className="font-mono text-xs text-slate-500">{d.DeviceRef}</span></TableCell>
                        <TableCell>
                          {currentSelection?.DeviceID === d.DeviceID ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-300" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Pagination */}
            {totalResults > 25 && (
              <div className="flex items-center justify-between text-sm text-slate-500">
                <span>Showing {page + 1}–{Math.min(page + 25, totalResults)} of {totalResults.toLocaleString()}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0 || searching} onClick={() => doSearch(Math.max(0, page - 25))}>Previous</Button>
                  <Button variant="outline" size="sm" disabled={page + 25 >= totalResults || searching} onClick={() => doSearch(page + 25)}>Next</Button>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ApplicationDetailDialog({ app, onClose }) {
  const appId = app.applicationId || '';
  const status = app.applicationStatus || '';
  const details = app.installerCustomerDetails || {};
  const supply = app.supplyDetails || {};
  const lcts = app.lcts || [];
  const helios = app._helios || {};

  // ---- Related emails (fetched lazily) -----------------------------------
  // We search MS365-synced inbox/sent + CRM email logs where the subject or
  // body references this application id (or the customer's DNO reference).
  // Lets admins see the whole DNO correspondence trail without leaving the
  // DNO Applications page.
  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(true);
  const [searchTerms, setSearchTerms] = useState([]);
  const [emailFilter, setEmailFilter] = useState('');
  const [expandedEmailKey, setExpandedEmailKey] = useState(null);

  useEffect(() => {
    if (!appId) { setEmails([]); setEmailsLoading(false); return; }
    let cancelled = false;
    setEmailsLoading(true);
    dnoAPI.getRelatedEmails(appId)
      .then((r) => {
        if (cancelled) return;
        setEmails(r.data?.emails || []);
        setSearchTerms(r.data?.related_to?.search_terms || []);
      })
      .catch(() => { if (!cancelled) setEmails([]); })
      .finally(() => { if (!cancelled) setEmailsLoading(false); });
    return () => { cancelled = true; };
  }, [appId]);

  // ---- Reply composer ----------------------------------------------------
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyTo, setReplyTo] = useState('');
  const [replyCc, setReplyCc] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [replyInReplyTo, setReplyInReplyTo] = useState(null);
  const [replyEditorMode, setReplyEditorMode] = useState('text');
  const [replyPreviewOpen, setReplyPreviewOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const openReplyComposer = (e) => {
    // Pre-fill: To = original sender (inbound) or original primary recipient
    // (outbound). Subject = "Re: <subject>" without duplicating an existing
    // Re: prefix. Body = HTML signature block + quoted original.
    const toAddr = e.direction === 'inbound'
      ? (e.from_address || (e.to_addresses || [])[0] || '')
      : ((e.to_addresses || [])[0] || e.from_address || '');
    const subj = (e.subject || '').replace(/^\s*Re\s*:\s*/i, '');
    const customerName = helios.customer_name || details.customerName || '';
    const customerAddr = details.mainAddress
      ? `${details.mainAddress}${details.installationPostCode ? ', ' + details.installationPostCode : ''}`
      : '';
    const dnoRefLine = helios.dno_reference
      ? `<p><strong>DNO Reference:</strong> ${helios.dno_reference}</p>`
      : '';
    const quotedDate = e.when ? new Date(e.when).toLocaleString('en-GB') : '';
    const quotedFrom = e.from_name
      ? `${e.from_name}${e.from_address ? ' &lt;' + e.from_address + '&gt;' : ''}`
      : (e.from_address || '');
    const quoted = e.preview
      ? `<blockquote style="border-left:3px solid #cbd5e1;padding:4px 12px;color:#475569;margin:8px 0;">
            <p style="font-size:12px;color:#64748b;margin:0 0 6px;">On ${quotedDate}, ${quotedFrom} wrote:</p>
            <p style="white-space:pre-wrap;margin:0;">${(e.preview || '').replace(/</g, '&lt;')}</p>
          </blockquote>`
      : '';
    setReplyTo(toAddr);
    setReplyCc('');
    setReplySubject(`Re: ${subj || 'DNO application ' + appId.slice(0, 12)}`);
    setReplyBody(
`<p>Hi${e.from_name ? ' ' + (e.from_name.split(' ')[0] || '') : ''},</p>
<p>&nbsp;</p>
<p>—</p>
<p><strong>Helios Electrical &amp; Solar</strong></p>
<p><strong>Customer:</strong> ${customerName}${customerAddr ? '<br/>' + customerAddr : ''}</p>
<p><strong>ENA Application:</strong> ${appId}</p>
${dnoRefLine}
${quoted}`
    );
    setReplyInReplyTo(e.ms365_id || null);
    setReplyOpen(true);
  };

  const sendReply = async () => {
    if (!replyTo.trim()) { toast.error('Please enter a recipient'); return; }
    if (!replySubject.trim()) { toast.error('Please enter a subject'); return; }
    if (!replyBody.trim()) { toast.error('Please write a reply'); return; }
    setSending(true);
    try {
      await api.post(`/dno/applications/${appId}/reply`, {
        to_addresses: replyTo.split(',').map(s => s.trim()).filter(Boolean),
        cc_addresses: replyCc.split(',').map(s => s.trim()).filter(Boolean),
        subject: replySubject,
        body_html: resolveBodyHtml(replyBody, replyEditorMode),
        in_reply_to_ms365_id: replyInReplyTo,
      });
      toast.success(`Reply sent to ${replyTo.split(',')[0]}`);
      setReplyOpen(false);
      // Re-fetch the related emails so the new outbound message shows up
      setEmailsLoading(true);
      try {
        const r = await dnoAPI.getRelatedEmails(appId);
        setEmails(r.data?.emails || []);
      } finally { setEmailsLoading(false); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reply failed');
    } finally { setSending(false); }
  };

  const filteredEmails = emailFilter.trim()
    ? emails.filter((e) => {
        const q = emailFilter.toLowerCase();
        return (
          (e.subject || '').toLowerCase().includes(q) ||
          (e.preview || '').toLowerCase().includes(q) ||
          (e.from_name || '').toLowerCase().includes(q) ||
          (e.from_address || '').toLowerCase().includes(q)
        );
      })
    : emails;

  return (
    <Dialog open={!!app} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="app-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Application Detail
          </DialogTitle>
          <DialogDescription>
            <span className="font-mono">{appId}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center gap-2">
            <StatusIcon status={status} />
            <Badge className={`border text-sm ${STATUS_STYLES[status] || 'bg-slate-100 text-slate-700 border-slate-300'}`}>
              {status}
            </Badge>
            {app.dno && <span className="text-sm text-slate-500 ml-2">DNO: {app.dno}</span>}
            {helios.dno_reference && (
              <Badge variant="outline" className="ml-1 text-xs font-mono" data-testid="app-dno-reference">
                DNO Ref: {helios.dno_reference}
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Customer</p>
              <p className="font-medium">{helios.customer_name || details.customerName || '-'}</p>
              <p className="text-slate-500">{details.customerEmail}</p>
              <p className="text-slate-500">{details.customerPhone}</p>
            </div>
            <div>
              <p className="text-slate-500">Address</p>
              <p className="font-medium">{details.mainAddress || '-'}</p>
              <p className="text-slate-500">{details.installationPostCode}</p>
              {details.mpan && <p className="text-slate-500">MPAN: {details.mpan}</p>}
            </div>
            <div>
              <p className="text-slate-500">Technologies</p>
              <div className="flex gap-1 flex-wrap mt-1">
                {lcts.map(l => (
                  <Badge key={l} variant="outline" className="text-xs">{LCT_LABELS[l] || l}</Badge>
                ))}
              </div>
            </div>
            <div>
              <p className="text-slate-500">Supply Details</p>
              <p>{supply.phaseCode === 1 ? 'Single' : 'Three'} Phase | {supply.premisesCutOutRating}A</p>
              <p>{supply.declaredVoltageAtConnectionPoint}</p>
            </div>
          </div>

          {app.devicesToInstall?.length > 0 && (
            <div>
              <p className="text-sm text-slate-500 mb-1">Devices to Install</p>
              {app.devicesToInstall.map((d, i) => (
                <div key={i} className="p-2 bg-slate-50 rounded text-sm mb-1">
                  <span className="font-medium">{d.deviceType}</span> — <span className="font-mono text-xs">{d.deviceSysRef}</span>
                  {d.registeredCapacity && <span className="text-slate-500 ml-2">{d.registeredCapacity}kW</span>}
                </div>
              ))}
            </div>
          )}

          {app.dnoApplicationResponse && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm font-semibold text-blue-800">DNO Response</p>
              <p className="text-sm text-blue-700">{app.dnoApplicationResponse.decision || ''}</p>
              {app.dnoApplicationResponse.comment && (
                <p className="text-sm text-blue-600 mt-1">{app.dnoApplicationResponse.comment}</p>
              )}
            </div>
          )}

          {(() => {
            // ENA v3 "DNO Works Required" reasons — surfaced so staff see
            // exactly why the application is blocked. ENA's key naming varies,
            // so read several likely variants defensively.
            const rawReasons =
              app.dnoWorksRequiredReasons ??
              app.worksRequiredReasons ??
              app.dnoApplicationResponse?.dnoWorksRequiredReasons ??
              app.dnoApplicationResponse?.worksRequiredReasons ??
              app.dnoApplicationResponse?.reasons ??
              null;
            let reasons = [];
            if (Array.isArray(rawReasons)) {
              reasons = rawReasons
                .map((r) => (typeof r === 'string' ? r : (r?.reason || r?.description || r?.text || r?.message || '')))
                .filter(Boolean);
            } else if (typeof rawReasons === 'string' && rawReasons.trim()) {
              reasons = [rawReasons.trim()];
            }
            if (reasons.length === 0) return null;
            return (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="app-works-required-reasons">
                <div className="flex items-center gap-2 mb-1.5">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <p className="text-sm font-semibold text-amber-800">DNO Works Required — Reasons</p>
                </div>
                <ul className="list-disc pl-6 space-y-1">
                  {reasons.map((r, i) => (
                    <li key={i} className="text-sm text-amber-800" data-testid={`app-works-required-reason-${i}`}>{r}</li>
                  ))}
                </ul>
              </div>
            );
          })()}

          {app.createdDate && (
            <p className="text-xs text-slate-400">Created: {new Date(app.createdDate).toLocaleString('en-GB')}</p>
          )}

          {/* ----- Related DNO emails ------------------------------------ */}
          <div className="border-t border-slate-200 pt-4" data-testid="app-related-emails-section">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-600" />
                <h3 className="text-sm font-semibold text-slate-700">
                  Related DNO Correspondence
                  <Badge variant="outline" className="ml-2 text-[10px]" data-testid="app-emails-count">
                    {emailsLoading ? '…' : emails.length}
                  </Badge>
                </h3>
              </div>
              {emails.length > 3 && (
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input
                    value={emailFilter}
                    onChange={(e) => setEmailFilter(e.target.value)}
                    placeholder="Filter emails…"
                    className="h-7 w-44 pl-7 text-xs"
                    data-testid="app-email-filter"
                  />
                </div>
              )}
            </div>
            {searchTerms.length > 0 && (
              <p className="text-[11px] text-slate-400 mb-2">
                Matching emails referencing{' '}
                {searchTerms.map((t, i) => (
                  <code key={t} className="px-1 py-0.5 bg-slate-100 rounded mx-0.5 font-mono">
                    {t.length > 16 ? `…${t.slice(-12)}` : t}{i < searchTerms.length - 1 ? '' : ''}
                  </code>
                ))}
              </p>
            )}
            {emailsLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
                <Loader2 className="w-4 h-4 animate-spin" /> Searching mailbox + CRM logs…
              </div>
            ) : filteredEmails.length === 0 ? (
              <div className="text-sm text-slate-500 italic py-3" data-testid="app-no-related-emails">
                {emails.length === 0
                  ? 'No emails reference this application yet. Once your DNO contact emails you back, anything mentioning the application ID or DNO reference will appear here automatically.'
                  : 'No emails match your filter.'}
              </div>
            ) : (
              <div className="space-y-1.5">
                {filteredEmails.map((e, idx) => {
                  const key = e.ms365_id || `${e.source}-${e.subject}-${e.when}-${idx}`;
                  const isOutbound = e.direction === 'outbound';
                  const expanded = expandedEmailKey === key;
                  return (
                    <div
                      key={key}
                      className={`p-2.5 rounded border transition-colors cursor-pointer ${
                        isOutbound
                          ? 'border-sky-200 bg-sky-50/40 hover:bg-sky-50'
                          : 'border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50'
                      }`}
                      onClick={() => setExpandedEmailKey(expanded ? null : key)}
                      data-testid={`related-email-${idx}`}
                    >
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className={`text-[10px] uppercase ${isOutbound ? 'border-sky-300 text-sky-700' : 'border-emerald-300 text-emerald-700'}`}>
                              {isOutbound ? 'Sent' : 'Received'}
                            </Badge>
                            <span className="text-sm font-medium text-slate-800 truncate">
                              {e.subject || '(no subject)'}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            {isOutbound
                              ? <>To <span className="font-medium">{(e.to_addresses || []).join(', ') || '—'}</span></>
                              : <>From <span className="font-medium">{e.from_name || e.from_address}</span> {e.from_name && e.from_address ? <>&lt;{e.from_address}&gt;</> : null}</>}
                            {e.when && <> · {new Date(e.when).toLocaleString('en-GB')}</>}
                            {e.mailbox && <> · {e.mailbox}</>}
                          </p>
                          {(expanded || !e.preview) ? null : (
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">{e.preview}</p>
                          )}
                          {expanded && e.preview && (
                            <p className="text-xs text-slate-700 mt-2 whitespace-pre-wrap leading-relaxed bg-white border border-slate-200 rounded p-2">
                              {e.preview}
                            </p>
                          )}
                          {expanded && e.cc_addresses?.length > 0 && (
                            <p className="text-[10px] text-slate-400 mt-1">CC: {e.cc_addresses.join(', ')}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(ev) => { ev.stopPropagation(); openReplyComposer(e); }}
                            className="h-7 text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                            data-testid={`reply-email-${idx}`}
                            title="Reply — pre-fills the original sender + application reference"
                          >
                            <Send className="w-3 h-3 mr-1" /> Reply
                          </Button>
                          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>

        {/* Reply-to-DNO composer ------------------------------------------ */}
        <Dialog open={replyOpen} onOpenChange={setReplyOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dno-reply-composer">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                Reply to DNO
              </DialogTitle>
              <DialogDescription>
                Sent from <span className="font-mono">DNO@helioses.co.uk</span> · application reference + customer details are pre-quoted so the DNO can thread your reply.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-xs text-slate-600 font-medium block mb-1">To <span className="text-rose-500">*</span></label>
                <Input
                  value={replyTo}
                  onChange={(ev) => setReplyTo(ev.target.value)}
                  placeholder="dno@example.com (comma-separated for multiple)"
                  className="h-9 text-sm"
                  data-testid="reply-to-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium block mb-1">CC</label>
                <Input
                  value={replyCc}
                  onChange={(ev) => setReplyCc(ev.target.value)}
                  placeholder="Optional CC recipients"
                  className="h-9 text-sm"
                  data-testid="reply-cc-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium block mb-1">Subject <span className="text-rose-500">*</span></label>
                <Input
                  value={replySubject}
                  onChange={(ev) => setReplySubject(ev.target.value)}
                  className="h-9 text-sm"
                  data-testid="reply-subject-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-slate-600 font-medium">Message <span className="text-rose-500">*</span></label>
                  <EmailModeToggle
                    mode={replyEditorMode}
                    onChange={setReplyEditorMode}
                    body={replyBody}
                    setBody={setReplyBody}
                    testIdPrefix="dno-reply-editor-mode"
                  />
                </div>
                <textarea
                  value={replyBody}
                  onChange={(ev) => setReplyBody(ev.target.value)}
                  className={`w-full min-h-[280px] p-3 border border-slate-200 rounded focus:bg-white focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-200 ${replyEditorMode === 'html' ? 'text-xs font-mono bg-slate-900 text-emerald-300' : 'text-sm bg-slate-50'}`}
                  placeholder="Write your reply…"
                  data-testid="reply-body-input"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  {replyEditorMode === 'html'
                    ? 'HTML mode — paste markup or hand-write it. The application reference auto-tags future searches.'
                    : 'Plain text — line breaks become paragraphs. Switch to HTML for full formatting.'}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReplyOpen(false)} disabled={sending}>Cancel</Button>
              <Button
                variant="outline"
                onClick={() => setReplyPreviewOpen(true)}
                disabled={sending || (!replySubject.trim() && !replyBody.trim())}
                data-testid="reply-preview-btn"
              >
                <Eye className="w-4 h-4 mr-1" />
                Preview
              </Button>
              <Button
                onClick={sendReply}
                disabled={sending}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="reply-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                {sending ? 'Sending…' : 'Send Reply'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <EmailPreviewDialog
          open={replyPreviewOpen}
          onOpenChange={setReplyPreviewOpen}
          to={replyTo}
          cc={replyCc}
          from="DNO@helioses.co.uk"
          subject={replySubject}
          bodyHtml={resolveBodyHtml(replyBody, replyEditorMode)}
          onConfirmSend={sendReply}
          sending={sending}
          recipientLabel="the DNO"
        />
      </DialogContent>
    </Dialog>
  );
}
